import { ApolloClient } from '@apollo/client';

import {
  CREATE_ADDRESS,
  DELETE_ADDRESS,
  UPDATE_ADDRESS,
  WEB3_GENERATE_NONCE,
  WEB3_VERIFY_SIGNATURE,
} from '../apollo/api/mutations';
import { GET_ADDRESSES } from '../apollo/api/queries';
import {
  AccountErrorResponse,
  AccountGateway,
  AddressInput,
} from '../types/Account';
import {
  GeneratedAddress,
  GeneratedCreateAddressMutation,
  GeneratedCreateAddressMutationVariables,
  GeneratedDeleteAddressMutation,
  GeneratedDeleteAddressMutationVariables,
  GeneratedGetAddressesQuery,
  GeneratedMutationWeb3GenerateNonceArgs,
  GeneratedMutationWeb3VerifySignatureArgs,
  GeneratedUpdateAddressMutation,
  GeneratedUpdateAddressMutationVariables,
  GeneratedWeb3GenerateNonceMutation,
  GeneratedWeb3VerifySignatureMutation,
} from '../types/generated';

class AvantArteAccountGateway implements AccountGateway {
  constructor(private client: ApolloClient<any>) {}

  async getAddresses(): Promise<
    Array<GeneratedAddress> | AccountErrorResponse
  > {
    const { data, errors } =
      await this.client.query<GeneratedGetAddressesQuery>({
        query: GET_ADDRESSES,
        fetchPolicy: 'no-cache',
      });

    if (errors && !data) {
      return {
        error: {
          code: errors[0]?.extensions?.code ?? 'UNKNOWN_ERROR',
          message: errors[0].message,
        },
      };
    }

    return data.addresses;
  }

  async deleteAddress(
    addressId: string,
  ): Promise<boolean | AccountErrorResponse> {
    const { data, errors } = await this.client.mutate<
      GeneratedDeleteAddressMutation,
      GeneratedDeleteAddressMutationVariables
    >({
      mutation: DELETE_ADDRESS,
      variables: {
        addressId,
      },
    });

    if (errors && !data) {
      return {
        error: {
          code: errors[0]?.extensions?.code ?? 'UNKNOWN_ERROR',
          message: errors[0].message,
        },
      };
    }

    return data!.deleteAddress;
  }

  async createAddress(
    address: AddressInput,
  ): Promise<GeneratedAddress | AccountErrorResponse> {
    const { data, errors } = await this.client.mutate<
      GeneratedCreateAddressMutation,
      GeneratedCreateAddressMutationVariables
    >({
      mutation: CREATE_ADDRESS,
      variables: { address },
    });
    if (errors && !data) {
      return {
        error: {
          code: errors[0]?.extensions?.code ?? 'UNKNOWN_ERROR',
          message: errors[0].message,
        },
      };
    }

    return data!.createAddress;
  }

  async updateAddress(
    addressId: string,
    changes: AddressInput,
  ): Promise<AccountErrorResponse | GeneratedAddress> {
    const { data, errors } = await this.client.mutate<
      GeneratedUpdateAddressMutation,
      GeneratedUpdateAddressMutationVariables
    >({
      mutation: UPDATE_ADDRESS,
      variables: { addressId, changes },
    });
    if (errors && !data) {
      return {
        error: {
          code: errors[0]?.extensions?.code ?? 'UNKNOWN_ERROR',
          message: errors[0].message,
        },
      };
    }

    return data!.updateAddress;
  }

  async web3VerifySignature(walletAddress: string, signature: string) {
    const { data, errors } = await this.client.mutate<
      GeneratedWeb3VerifySignatureMutation,
      GeneratedMutationWeb3VerifySignatureArgs
    >({
      mutation: WEB3_VERIFY_SIGNATURE,
      variables: {
        walletAddress,
        signature,
      },
    });

    if (errors && !data) {
      throw new Error(errors[0].message);
    }

    return data!.web3VerifySignature;
  }

  async web3GenerateNonce(walletAddress: string, message: string) {
    const { data, errors } = await this.client.mutate<
      GeneratedWeb3GenerateNonceMutation,
      GeneratedMutationWeb3GenerateNonceArgs
    >({
      mutation: WEB3_GENERATE_NONCE,
      variables: {
        walletAddress,
        message,
      },
    });

    if (errors && !data) {
      throw new Error(errors[0].message);
    }

    return data!.web3GenerateNonce;
  }
}

export default AvantArteAccountGateway;
